<script setup>
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import SpinnerIcon from "@js/components/icons/SpinnerIcon.vue";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";
import DeleteCallbackRequestAction from "@frontJs/components/actions/DeleteCallbackRequestAction.vue";

const { t } = useI18n();

const { callbackRequest, callback } = defineProps({
  callbackRequest: {
    type: Object,
    required: true,
  },
  callback: {
    type: Function,
    required: false,
  },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
  comment: callbackRequest.comment,
});

const validationSchema = object({
  comment: string().nullable(),
});

const isOpen = ref(false);

function setIsOpen(value) {
  isOpen.value = value;
}

function saveAction() {
  isSubmitting.value = true;

  fetch(
    route("front.postCallbackRequest", {
      callbackRequest: callbackRequest.id,
    }),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        comment: form.comment,
      }),
    },
  )
    .then((response) => response.json())
    .then((data) => {
      setIsOpen(false);

      if (callback) {
        callback();
      }

      form.comment = data.internalComment;
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      console.error(error);
    });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen" size="extra-large">
    <template #title>
      {{ t("title.update") }}
    </template>

    <template #full-content>
      <Form
        :validation-schema="validationSchema"
        @submit="saveAction"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="h-full max-h-[calc(100vh-8rem)] overflow-y-auto px-6 py-4">
          <WysiwygField
            v-model="form.comment"
            :label="t('label.comment')"
            :placeholder="t('label.comment')"
            field-name="comment"
            field-id="comment"
          />
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
        >
          <DeleteCallbackRequestAction :callback-request="callbackRequest" :callback="callback">
            <template #button="{ setIsOpen }">
              <ActionButton
                type="transparent"
                :callback="() => setIsOpen(true)"
              >
                {{ t("button.delete") }}
              </ActionButton>
            </template>
          </DeleteCallbackRequestAction>

          <ActionButton
            submit
            :disabled="Object.keys(errors).length > 0 || isSubmitting"
          >
            <template v-if="!isSubmitting">
              {{ t("button.save") }}
            </template>

            <template v-else>
              <SpinnerIcon
                class="mr-1 size-4 animate-spin fill-white text-gray-200"
              />

              {{ t("button.saveInProgress") }}
            </template>
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
